import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

const profileSchema = z.object({
  use_case: z.enum(
    ["seasonal_storage", "moving", "organizing", "long_term", "other"],
    { required_error: "Please select how you plan to use storage" }
  ),
  signup_source: z.enum(
    ["google", "social_media", "friend", "advertisement", "other"],
    { required_error: "Please tell us how you heard about us" }
  ),
  zip: z.string()
    .trim()
    .regex(/^\d{5}$/, "ZIP code must be exactly 5 digits")
    .optional()
    .or(z.literal("")),
});

type ProfileFormData = z.infer<typeof profileSchema>;

const Onboarding = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const form = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      use_case: undefined,
      signup_source: undefined,
      zip: "",
    },
  });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/auth");
        return;
      }

      const { data: profile } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();

      if (profile) {
        form.reset({
          use_case: profile.use_case as any,
          signup_source: profile.signup_source as any,
          zip: profile.zip || "",
        });

        // If profile is already complete, redirect to dashboard
        if (profile.onboarding_completed) {
          navigate("/dashboard");
          return;
        }
      }
    } catch (error) {
      console.error("Error loading profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: ProfileFormData) => {
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No user found");

      const { error } = await supabase
        .from("profiles")
        .update({
          use_case: data.use_case,
          signup_source: data.signup_source,
          zip: data.zip || null,
          onboarding_completed: true,
          updated_at: new Date().toISOString(),
        })
        .eq("id", user.id);

      if (error) throw error;

      navigate("/dashboard");
    } catch (error) {
      console.error("Error updating profile:", error);
      toast({
        title: "Error",
        description: "Failed to save profile",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <>
          <CardHeader className="text-center">
              <CardTitle className="text-3xl">Welcome to Endless Storage! 📦</CardTitle>
              <CardDescription className="text-base mt-2">
                Let's get you started in just 3 quick questions
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <FormField
                    control={form.control}
                    name="use_case"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-base">How are you planning to use Endless Storage?</FormLabel>
                        <FormControl>
                          <div className="grid gap-3">
                            {[
                              { value: "seasonal_storage", label: "Seasonal Storage", desc: "Holiday decor, sports equipment, etc." },
                              { value: "moving", label: "Moving/Relocation", desc: "Temporary storage during a move" },
                              { value: "organizing", label: "Decluttering/Organizing", desc: "Free up space at home" },
                              { value: "long_term", label: "Long-term Storage", desc: "Extended storage needs" },
                              { value: "other", label: "Other", desc: "Something else" },
                            ].map((option) => (
                              <div
                                key={option.value}
                                className={cn(
                                  "flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-all",
                                  field.value === option.value
                                    ? "border-primary bg-primary/5"
                                    : "border-border hover:border-primary/50"
                                )}
                                onClick={() => field.onChange(option.value)}
                              >
                                <div className="flex items-center h-5">
                                  <div className={cn(
                                    "h-4 w-4 rounded-full border-2 flex items-center justify-center",
                                    field.value === option.value ? "border-primary" : "border-muted-foreground"
                                  )}>
                                    {field.value === option.value && (
                                      <div className="h-2 w-2 rounded-full bg-primary" />
                                    )}
                                  </div>
                                </div>
                                <div className="flex-1">
                                  <div className="font-medium text-sm">{option.label}</div>
                                  <div className="text-xs text-muted-foreground">{option.desc}</div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="signup_source"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-base">How did you hear about us?</FormLabel>
                        <FormControl>
                          <div className="grid gap-3">
                            {[
                              { value: "google", label: "Google Search" },
                              { value: "social_media", label: "Social Media" },
                              { value: "friend", label: "Friend/Family" },
                              { value: "advertisement", label: "Advertisement" },
                              { value: "other", label: "Other" },
                            ].map((option) => (
                              <div
                                key={option.value}
                                className={cn(
                                  "flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-all",
                                  field.value === option.value
                                    ? "border-primary bg-primary/5"
                                    : "border-border hover:border-primary/50"
                                )}
                                onClick={() => field.onChange(option.value)}
                              >
                                <div className={cn(
                                  "h-4 w-4 rounded-full border-2 flex items-center justify-center",
                                  field.value === option.value ? "border-primary" : "border-muted-foreground"
                                )}>
                                  {field.value === option.value && (
                                    <div className="h-2 w-2 rounded-full bg-primary" />
                                  )}
                                </div>
                                <div className="font-medium text-sm">{option.label}</div>
                              </div>
                            ))}
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="zip"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-base">What's your ZIP code? (Optional)</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="12345" 
                            {...field}
                            maxLength={5}
                            onChange={(e) => {
                              const value = e.target.value.replace(/\D/g, '');
                              field.onChange(value);
                            }}
                          />
                        </FormControl>
                        <p className="text-xs text-muted-foreground">This helps us ensure service availability in your area</p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button type="submit" className="w-full" disabled={saving}>
                    {saving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      "Get Started"
                    )}
                  </Button>
                </form>
              </Form>
            </CardContent>
        </>
      </Card>
    </div>
  );
};

export default Onboarding;
